import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../store";
import {
  getAllProductsAndRawMaterials,
  getBOMPriceList,
  updateB2BPrice,
} from "../../store/slices/priceListSlice";
import Loading from "../../components/AssetsComponent/loading";
import { motion } from "framer-motion";
import PaginationControls from "../../components/pagination/PaginationControls";
import SearchInput from "../../components/Search/SearchInput";
import { Search } from "lucide-react";
import EditPriceListModal from "../../components/priceList/EditPriceListModal";
import toast from "react-hot-toast";
import Button, { ButtonType } from "../../components/Assets/Button";
import CreateBOM from "../BillOfMaterials/CreateBOM";
import { formatToDDMMYYYY } from "../../utils/formatDateUtils";
import { useNavigate } from "react-router-dom";
import { setSelectedBOM } from "../../store/slices/bomSlice";

type TabType = "products" | "rawMaterials" | "boms";

const PriceList: React.FC = () => {
  const dispatch: RootDispatch = useDispatch();

  const [activeTab, setActiveTab] = useState<TabType>("boms");
  const [search, setSearch] = useState("");
  const [bomSearch, setBomSearch] = useState("");

  const [productPage, setProductPage] = useState(1);
  const [rawMaterialPage, setRawMaterialPage] = useState(1);
  const [editItem, setEditItem] = useState<any>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateBOMOpen, setIsCreateBOMOpen] = useState(false);

  const { boms } = useSelector((state: RootState) => state.priceList);

  const {
    products,
    rawMaterials,
    productPagination,
    rawMaterialPagination,
    loading,
  } = useSelector((state: RootState) => state.priceList);

  const navigate = useNavigate();

  const handleViewBOM = (bom: any) => {
    dispatch(setSelectedBOM(bom));
    navigate(`/price-list-boms/${bom._id}`, {
      state: { from: "priceList" },
    });
  };

  useEffect(() => {
    if (activeTab === "boms") {
      dispatch(getBOMPriceList({ search: bomSearch }));
    } else {
      dispatch(
        getAllProductsAndRawMaterials({
          search,
          productPage,
          productLimit: 10,
          rawMaterialPage,
          rawMaterialLimit: 10,
        }),
      );
    }
  }, [search, bomSearch, productPage, rawMaterialPage, activeTab, dispatch]);

  const currentData =
    activeTab === "products"
      ? products
      : activeTab === "rawMaterials"
        ? rawMaterials
        : boms;

  const pagination =
    activeTab === "products" ? productPagination : rawMaterialPagination;

  const handleUpdatePrice = (price: number, margin: number) => {
    if (price < 0 || margin < 0) {
      return toast.error("Price cannot be negative");
    }
    if (margin > 99) {
      return toast.error("Margin cannot be more than 100%");
    }
    dispatch(
      updateB2BPrice({
        _id: editItem._id,
        type: editItem.type,
        b2bPrice: price,
        b2bMargin: margin,
      }),
    );

    setIsEditModalOpen(false);
  };

  return (
    <div className="p-6">
      <div className="flex items-center mb-6">
        {activeTab === "boms" ? (
          <div className="relative w-96 shrink-0">
            <Search className="absolute left-3 top-5 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <SearchInput
              placeholder="Search BOMs by name..."
              value={bomSearch}
              onSearch={(val) => {
                setBomSearch(val);
              }}
              className="w-full pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-md 
                focus:ring-1 focus:ring-navyBlue-primary focus:border-navyBlue-primary 
                outline-none transition-all text-sm text-gray-700 
                placeholder:text-gray-400 shadow-sm"
            />
          </div>
        ) : (
          <div className="relative w-96 shrink-0">
            <Search className="absolute left-3 top-5 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <SearchInput
              placeholder="Search products or raw materials..."
              value={search}
              onSearch={(val) => {
                setSearch(val);
                setProductPage(1);
                setRawMaterialPage(1);
              }}
              className="w-full pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-md 
                focus:ring-1 focus:ring-navyBlue-primary focus:border-navyBlue-primary 
                outline-none transition-all text-sm text-gray-700 
                placeholder:text-gray-400 shadow-sm"
            />
          </div>
        )}

        <div className="bg-white rounded-xl shadow-sm ml-6 px-4 py-2">
          <div className="flex items-center gap-4">
            {[
              { key: "boms", label: "Kits" },
              { key: "products", label: "Products" },
              { key: "rawMaterials", label: "Raw Materials" },
            ].map((tab) => {
              const isActive = activeTab === tab.key;

              return (
                <motion.button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key as TabType)}
                  className={`relative px-5 py-2 text-sm font-semibold rounded-lg
                ${
                  isActive
                    ? "text-blue-600"
                    : "text-gray-500 hover:text-gray-800"
                }`}
                >
                  {tab.label}

                  {isActive && (
                    <motion.div
                      layoutId="tab-indicator"
                      className="absolute -bottom-1 left-2 right-2 h-[2px] bg-blue-500"
                    />
                  )}
                </motion.button>
              );
            })}
          </div>
        </div>
        <Button
          type={ButtonType.Submit}
          onClick={() => setIsCreateBOMOpen(true)}
          className="ml-4 text-sm
          "
        >
          Create Kit
        </Button>
      </div>
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
            <tr>
              <th className="px-6 py-4 text-left">Name</th>
              <th className="px-6 py-4 text-left">Composition</th>
              <th className="px-6 py-4 text-left">No Of Panels</th>
              <th className="px-6 py-4 text-left">Created Date</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={4} className="py-10">
                  <div className="flex items-center justify-center">
                    <Loading />
                  </div>
                </td>
              </tr>
            ) : activeTab === "boms" ? (
              currentData.length > 0 ? (
                currentData.map((bom: any) => (
                  <tr key={bom._id} className="border-t hover:bg-gray-50">
                    <td
                      className="px-6 py-4 font-medium cursor-pointer"
                      onClick={() => handleViewBOM(bom)}
                    >
                      {bom.name}
                    </td>

                    <td className="px-6 py-4">
                      <div>{bom.items.length} Items</div>
                      <div>
                        {
                          bom.items.filter((i: any) => i.type === "product")
                            .length
                        }{" "}
                        Prod |{" "}
                        {
                          bom.items.filter((i: any) => i.type === "material")
                            .length
                        }{" "}
                        Mat
                      </div>
                    </td>

                    <td className="px-6 py-4">{bom.noOfPanels}</td>

                    <td className="px-6 py-4">
                      {formatToDDMMYYYY(new Date(bom.createdAt))}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="text-center py-10 text-gray-400">
                    {bomSearch
                      ? `No BOMs found matching "${bomSearch}"`
                      : "No BOMs available"}
                  </td>
                </tr>
              )
            ) : activeTab === "products" ? (
              products.length > 0 ? (
                products.map((item) => (
                  <tr key={item._id} className="border-t hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium">
                      {item.productName}
                    </td>

                    <td className="px-6 py-4 text-green-600 font-semibold">
                      ₹ {item.b2bPrice || "-"}
                    </td>

                    <td className="px-6 py-4 text-green-600 font-semibold">
                      ₹ {item.b2bMargin || "-"}
                    </td>

                    <td className="px-6 py-4">
                      <button
                        onClick={() => {
                          setEditItem({ ...item, type: "product" });
                          setIsEditModalOpen(true);
                        }}
                        className="text-blue-600 hover:underline text-sm font-medium"
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="text-center py-10 text-gray-400">
                    No products available
                  </td>
                </tr>
              )
            ) : activeTab === "rawMaterials" ? (
              rawMaterials.length > 0 ? (
                rawMaterials.map((item) => (
                  <tr key={item._id} className="border-t hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium">{item.name}</td>

                    <td className="px-6 py-4 text-green-600 font-semibold">
                      ₹ {item.b2bPrice || "-"}
                    </td>

                    <td className="px-6 py-4 text-green-600 font-semibold">
                      ₹ {item.b2bMargin || "-"}
                    </td>

                    <td className="px-6 py-4">
                      <button
                        onClick={() => {
                          setEditItem({ ...item, type: "rawMaterial" });
                          setIsEditModalOpen(true);
                        }}
                        className="text-blue-600 hover:underline text-sm font-medium"
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="text-center py-10 text-gray-400">
                    No raw materials available
                  </td>
                </tr>
              )
            ) : null}
          </tbody>
        </table>
      </div>

      {pagination && (
        <PaginationControls
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          onNext={() => {
            if (activeTab === "products") {
              setProductPage((p) => p + 1);
            } else {
              setRawMaterialPage((p) => p + 1);
            }
          }}
          onPrev={() => {
            if (activeTab === "products") {
              setProductPage((p) => p - 1);
            } else {
              setRawMaterialPage((p) => p - 1);
            }
          }}
        />
      )}

      <EditPriceListModal
        open={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        initialPrice={editItem?.b2bPrice}
        initalMargin={editItem?.b2bMargin}
        title={`Edit ${editItem?.type === "product" ? "Product" : "Raw Material"} Price`}
        onSave={handleUpdatePrice}
      />
      {isCreateBOMOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 flex justify-between items-center">
              <h2 className="text-lg font-semibold">
                Create BOM (From Price List)
              </h2>
              <button
                onClick={() => setIsCreateBOMOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ×
              </button>
            </div>

            <CreateBOM
              isEdit={false}
              via="priceList"
              onSuccess={() => {
                setIsCreateBOMOpen(false);
                dispatch(getAllProductsAndRawMaterials({}));
              }}
              onCancel={() => setIsCreateBOMOpen(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default PriceList;
